// ─────────────────────────────────────────────
// KLOTHIQ — Product & Variant Zod Schemas
// ─────────────────────────────────────────────

import { z } from 'zod';
import { moneySchema, moneyOrZeroSchema, nonNegativeIntSchema } from './common.schema';

export const productFormSchema = z.object({
  product_code: z
    .string()
    .min(1, 'Product code is required')
    .max(160, 'Product code must be 160 characters or less')
    .regex(/^[A-Za-z0-9][A-Za-z0-9-]*$/, 'Only letters, numbers, and hyphens allowed'),
  name: z.string().min(1, 'Product name is required').max(200, 'Name too long'),
  category_id: z.string().uuid().nullable().optional(),
  team: z.string().max(100).nullable().optional(),
  sleeve: z.string().max(50).nullable().optional(),
  material: z.string().max(100).nullable().optional(),
  description: z.string().max(1000).nullable().optional(),
  is_active: z.boolean().default(true),
});

export const variantFormSchema = z.object({
  sku: z
    .string()
    .min(1, 'SKU is required')
    .max(512, 'SKU must be 512 characters or less')
    .regex(/^[^\u0000-\u001f\u007f]+$/, 'SKU cannot contain control characters'),
  size: z.string().min(1, 'Size is required').max(20),
  color: z.string().max(50).nullable().optional(),
  default_cost: moneySchema,
  default_price: moneySchema,
  low_stock_level: nonNegativeIntSchema.default(5),
  is_active: z.boolean().default(true),
});

export const productWithVariantsSchema = productFormSchema.extend({
  variants: z.array(variantFormSchema).min(1, 'At least one variant is required'),
});

export const categoryFormSchema = z.object({
  name: z.string().min(1, 'Category name is required').max(100),
  parent_id: z.string().uuid().nullable().optional(),
  is_active: z.boolean().default(true),
});

export const openingStockSchema = z.object({
  variant_id: z.string().uuid('Select a variant'),
  quantity: z.number().int().positive('Quantity must be greater than zero'),
  unit_cost: moneyOrZeroSchema,
  reason: z.string().default('Opening stock'),
});

export type ProductFormValues = z.infer<typeof productFormSchema>;
export type VariantFormValues = z.infer<typeof variantFormSchema>;
export type ProductWithVariantsValues = z.infer<typeof productWithVariantsSchema>;
export type CategoryFormValues = z.infer<typeof categoryFormSchema>;
export type OpeningStockValues = z.infer<typeof openingStockSchema>;
